import { InsightsSection } from '@percent/lemonade'

import { InsightsDomain } from './context/InsightsDomain'
import {
  InsightsBarChart,
  InsightsDonutChart,
  InsightsLineChart,
  InsightsAreaChart,
  InsightsAutoChart,
  InsightsMetricChart,
  InsightsStat,
} from './components/charts/charts'
import { InsightsFilters } from './components/InsightsFilters/InsightsFilters'
import { InsightsDataset } from './components/InsightsDataset/InsightsDataset'
import { InsightsExport } from './components/InsightsExport/InsightsExport'

// Types — the frontend mirror of the pbp-api insights platform contract.
export * from './types/insights.types'

/**
 * The insights component namespace. Compose a domain-scoped panel declaratively;
 * pick the chart type by which component you render (a `rows` metric can be a bar
 * or a donut — your choice):
 *
 * ```tsx
 * <Insights.Domain domain="donations" service={insightsService} colourMap={colours}>
 *   <Insights.Filters />
 *   <Insights.Section title="Donation summary">
 * ```
 *
 * Scope a panel by locking values on the domain (always applied, controls hidden)
 * and/or whitelisting which controls the bar shows, in order:
 *
 * ```tsx
 * <Insights.Domain domain="donations" service={service} lockedFilters={{ category: ['donations'] }}>
 *   <Insights.Filters visibleFilters={['dateRange', 'activityTags']} />
 *   <Insights.Section title="Donation summary">
 *     <Insights.BarChart heroMetric="summary" metric="by-cause-area" />
 *     <Insights.BarChart metric="by-organisation" orientation="horizontal" />
 *     <Insights.DonutChart metric="by-region" />
 *   </Insights.Section>
 * </Insights.Domain>
 * ```
 *
 * - `Stat` — a headline scalar metric (value + trend), no chart.
 * - `BarChart` / `DonutChart` — typed charts; each takes a `metric` and an
 *   optional `heroMetric` for a headline value above the chart.
 * - `AutoChart` — picks the chart from the data shape.
 * - `Dataset` — a paginated, sortable, filterable table of a dataset's rows.
 *
 * Namespaced so the names don't collide with Lemonade's `StatCard` / chart
 * primitives that these compose. `Section` is Lemonade's `InsightsSection`
 * layout shell, surfaced here so the whole panel composes under one namespace.
 */
export const Insights = {
  Domain: InsightsDomain,
  Section: InsightsSection,
  Filters: InsightsFilters,
  Stat: InsightsStat,
  BarChart: InsightsBarChart,
  DonutChart: InsightsDonutChart,
  LineChart: InsightsLineChart,
  AreaChart: InsightsAreaChart,
  AutoChart: InsightsAutoChart,
  MetricChart: InsightsMetricChart,
  Dataset: InsightsDataset,
  Export: InsightsExport,
}

// Context & service contract.
export { InsightsContext, useInsightsContext } from './context/InsightsContext'
export type {
  InsightsService,
  InsightsContextValue,
} from './context/InsightsContext'
export type { InsightsDomainProps } from './context/InsightsDomain'

// Hooks.
export { useInsightsMetric } from './hooks/useInsightsMetric'
export type {
  UseInsightsMetricOptions,
  UseInsightsMetricResult,
} from './hooks/useInsightsMetric'
export { useFilters } from './hooks/useFilters'
export type { UseFiltersResult } from './hooks/useFilters'
export { useInsightsDataset } from './hooks/useInsightsDataset'
export type {
  DatasetSort,
  DatasetSortDirection,
  UseInsightsDatasetOptions,
  UseInsightsDatasetResult,
} from './hooks/useInsightsDataset'
export { useInsightsDatasetExport } from './hooks/useInsightsDatasetExport'
export type {
  ExportCsvArgs,
  UseInsightsDatasetExportResult,
} from './hooks/useInsightsDatasetExport'

// Component prop types (the components themselves are reached via the `Insights` namespace).
export type {
  InsightsBarChartProps,
  InsightsDonutChartProps,
  InsightsLineChartProps,
  InsightsAreaChartProps,
  InsightsAutoChartProps,
  InsightsMetricChartProps,
  InsightsStatProps,
  SuggestedChart,
} from './components/charts/charts'
export { specForSuggestedChart } from './components/charts/charts'
export type {
  InsightsFiltersProps,
  InsightsFilterControlApi,
} from './components/InsightsFilters/InsightsFilters'
export type {
  InsightsDatasetProps,
  InsightsDatasetLabels,
  DatasetColumnConfig,
  DatasetPageSizeConfig,
} from './components/InsightsDataset/InsightsDataset'
export type {
  InsightsExportProps,
  InsightsExportLabels,
} from './components/InsightsExport/InsightsExport'
export { InsightsDateRange } from './components/InsightsDateRange/InsightsDateRange'
export type {
  InsightsDateRangeLabels,
  InsightsDateRangeProps,
} from './components/InsightsDateRange/InsightsDateRange'
export { InsightsLegend } from './components/InsightsLegend/InsightsLegend'

// Extensibility: pair a custom ChartSpec with MetricView to add a new chart type.
export { MetricView } from './components/MetricView/MetricView'
export type {
  ChartSpec,
  MetricViewProps,
} from './components/MetricView/MetricView'
export {
  BarChartBody,
  DonutChartBody,
  LineChartBody,
  AreaChartBody,
} from './components/charts/chartBodies'
export type {
  ChartBodyProps,
  AreaChartBodyProps,
} from './components/charts/chartBodies'

// Filter adapters.
export { urlFilterAdapter } from './adapters/urlFilterAdapter'
export { createMemoryFilterAdapter } from './adapters/memoryFilterAdapter'
export type {
  FilterAdapter,
  FilterFieldSpec,
  FilterStateController,
} from './adapters/filterAdapter.types'

// Utilities exposed for host wiring (the app's insightsService) and advanced use.
export {
  serialiseFiltersForApi,
  deserialiseFilters,
  hasActiveValues,
} from './utils/filterSerialiser'
export { downloadCsv, toCsvFileName } from './utils/downloadCsv'
export { CHART_PALETTE_SEQUENCE, resolveColour } from './utils/colourResolver'
export type { InsightsColourMap } from './utils/colourResolver'
export {
  formatValueHero,
  formatTrend,
  formatDuration,
  formatCountShort,
  tickFormatterFor,
} from './utils/formatValue'
